'use client';

import { useState, useCallback, useRef } from 'react';
import { 
  ArrowLeft, 
  Sparkles, 
  Edit2, 
  Copy, 
  Clock,
  ExternalLink,
  Save,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { AskAIBar } from '@/components/ai';
import { RichTextEditor } from '@/components/shared/lexical-editor';
import { useToast } from '@/hooks/use-toast';
import type { DiscoveryCompetitor } from '@/hooks/use-discovery';

interface CompetitorDetailViewProps {
  competitor: DiscoveryCompetitor;
  projectName?: string;
  onBack: () => void;
  onSave?: (data: Partial<DiscoveryCompetitor>) => Promise<void>;
  onAskAI?: (message: string) => Promise<void>;
  onCopy?: () => void;
}

const threatColors: Record<string, string> = {
  HIGH: 'bg-red-100 text-red-700 border-red-200',
  MEDIUM: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  LOW: 'bg-green-100 text-green-700 border-green-200',
};

const categoryColors: Record<string, string> = {
  DIRECT: 'bg-red-100 text-red-700',
  INDIRECT: 'bg-orange-100 text-orange-700',
  SUBSTITUTE: 'bg-blue-100 text-blue-700',
};

/** Build a single HTML document from all competitor fields */
function buildCompetitorHtml(c: DiscoveryCompetitor): string {
  const parts: string[] = [];
  parts.push(`<h1>${c.name || 'Unnamed Competitor'}</h1>`);
  if (c.description) parts.push(`<p>${c.description.replace(/\n/g, '<br/>')}</p>`);
  const companyDetails = [c.foundedYear, c.headquarters, c.employeeCount, c.fundingStatus].filter(Boolean);
  if (companyDetails.length > 0) {
    parts.push(`<h2>Company Profile</h2>`);
    if (c.foundedYear) parts.push(`<p><strong>Founded:</strong> ${c.foundedYear}</p>`);
    if (c.headquarters) parts.push(`<p><strong>Headquarters:</strong> ${c.headquarters}</p>`);
    if (c.employeeCount) parts.push(`<p><strong>Employees:</strong> ${c.employeeCount}</p>`);
    if (c.fundingStatus) parts.push(`<p><strong>Funding:</strong> ${c.fundingStatus}</p>`);
  }
  if (c.productOffering || c.targetAudience || c.pricingModel || c.marketPosition || c.marketShare) {
    parts.push(`<h2>Product &amp; Market</h2>`);
    if (c.productOffering) parts.push(`<h3>Product Offering</h3><p>${c.productOffering.replace(/\n/g, '<br/>')}</p>`);
    if (c.targetAudience) parts.push(`<h3>Target Audience</h3><p>${c.targetAudience.replace(/\n/g, '<br/>')}</p>`);
    if (c.pricingModel) parts.push(`<h3>Pricing Model</h3><p>${c.pricingModel.replace(/\n/g, '<br/>')}</p>`);
    if (c.marketPosition) parts.push(`<h3>Market Position</h3><p>${c.marketPosition.replace(/\n/g, '<br/>')}</p>`);
    if (c.marketShare) parts.push(`<h3>Market Share</h3><p>${c.marketShare}</p>`);
  }
  if (c.keyDifferentiators || c.strengths || c.weaknesses || c.technologyStack || c.customerSentiment) {
    parts.push(`<h2>Competitive Analysis</h2>`);
    if (c.keyDifferentiators) parts.push(`<h3>Key Differentiators</h3><p>${c.keyDifferentiators.replace(/\n/g, '<br/>')}</p>`);
    if (c.strengths) parts.push(`<h3>Strengths</h3><p>${c.strengths.replace(/\n/g, '<br/>')}</p>`);
    if (c.weaknesses) parts.push(`<h3>Weaknesses</h3><p>${c.weaknesses.replace(/\n/g, '<br/>')}</p>`);
    if (c.technologyStack) parts.push(`<h3>Technology Stack</h3><p>${c.technologyStack.replace(/\n/g, '<br/>')}</p>`);
    if (c.customerSentiment) parts.push(`<h3>Customer Sentiment</h3><p>${c.customerSentiment.replace(/\n/g, '<br/>')}</p>`);
  }
  if (c.opportunities || c.threats || c.recentNews || c.strategicMoves) {
    parts.push(`<h2>Strategic Assessment</h2>`);
    if (c.opportunities) parts.push(`<h3>Opportunities</h3><p>${c.opportunities.replace(/\n/g, '<br/>')}</p>`);
    if (c.threats) parts.push(`<h3>Threats</h3><p>${c.threats.replace(/\n/g, '<br/>')}</p>`);
    if (c.recentNews) parts.push(`<h3>Recent News</h3><p>${c.recentNews.replace(/\n/g, '<br/>')}</p>`);
    if (c.strategicMoves) parts.push(`<h3>Strategic Moves</h3><p>${c.strategicMoves.replace(/\n/g, '<br/>')}</p>`);
  }
  return parts.join('\n');
}

/** Parse HTML from the editor back into fields for backend save */
function parseCompetitorHtml(html: string): { fields: Partial<DiscoveryCompetitor>; html: string } {
  const div = document.createElement('div');
  div.innerHTML = html;
  const text = (el: Element | null): string => el?.textContent?.trim() || '';

  const h1 = div.querySelector('h1');
  const fields: Partial<DiscoveryCompetitor> = { name: text(h1) };

  const getContentAfter = (heading: Element): string => {
    const parts: string[] = [];
    let sibling = heading.nextElementSibling;
    while (sibling && !['H1', 'H2', 'H3'].includes(sibling.tagName)) {
      parts.push(sibling.textContent?.trim() || '');
      sibling = sibling.nextElementSibling;
    }
    return parts.filter(Boolean).join('\n');
  };

  const h3s = div.querySelectorAll('h3');
  h3s.forEach((h3) => {
    const name = text(h3).toLowerCase();
    const content = getContentAfter(h3);

    if (name.includes('product offering')) fields.productOffering = content;
    else if (name.includes('target audience')) fields.targetAudience = content;
    else if (name.includes('pricing')) fields.pricingModel = content;
    else if (name.includes('market position')) fields.marketPosition = content;
    else if (name.includes('market share')) fields.marketShare = content;
    else if (name.includes('key differentiator')) fields.keyDifferentiators = content;
    else if (name.includes('strength')) fields.strengths = content;
    else if (name.includes('weakness')) fields.weaknesses = content;
    else if (name.includes('technology')) fields.technologyStack = content;
    else if (name.includes('customer sentiment')) fields.customerSentiment = content;
    else if (name.includes('opportunit')) fields.opportunities = content;
    else if (name.includes('threat')) fields.threats = content;
    else if (name.includes('recent news')) fields.recentNews = content;
    else if (name.includes('strategic move')) fields.strategicMoves = content;
  });

  if (h1) {
    const descParts: string[] = [];
    let sibling = h1.nextElementSibling;
    while (sibling && sibling.tagName !== 'H2') {
      descParts.push(sibling.textContent?.trim() || '');
      sibling = sibling.nextElementSibling;
    }
    const desc = descParts.filter(Boolean).join('\n');
    if (desc) fields.description = desc;
  }

  return { fields, html };
}

export function CompetitorDetailView({
  competitor,
  projectName = 'Project',
  onBack,
  onSave,
  onAskAI,
  onCopy,
}: CompetitorDetailViewProps) {
  const { toast } = useToast();
  const [showAskAI, setShowAskAI] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedHtml, setEditedHtml] = useState('');
  const [savedData, setSavedData] = useState<Partial<DiscoveryCompetitor> | null>(null);
  const [savedHtml, setSavedHtml] = useState('');
  const initialEditorHtmlRef = useRef('');
  const handleEditorChange = useCallback((html: string) => {
    if (!initialEditorHtmlRef.current) {
      initialEditorHtmlRef.current = html;
    }
    setEditedHtml(html);
  }, []);

  const hasChanges = (): boolean => isEditing && editedHtml !== '' && editedHtml !== initialEditorHtmlRef.current;

  const display = savedData ? { ...competitor, ...savedData } as DiscoveryCompetitor : competitor;

  const handleSaveChanges = async () => {
    if (!onSave || !editedHtml) return;
    setIsSaving(true);
    try {
      const { fields, html } = parseCompetitorHtml(editedHtml);
      await onSave(fields);
      setSavedData(fields);
      setSavedHtml(html);
      setIsEditing(false);
      setEditedHtml('');
      initialEditorHtmlRef.current = '';
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedHtml('');
    initialEditorHtmlRef.current = '';
  };

  const handleAskAI = async (message: string) => {
    if (onAskAI) {
      await onAskAI(message);
    }
    return { response: '', citations: [] };
  };

  const handleCopy = () => {
    const content = JSON.stringify(competitor, null, 2);
    navigator.clipboard.writeText(content);
    onCopy?.();
    toast({
      title: 'Copied',
      description: 'Competitor analysis copied to clipboard',
    });
  };

  // Get metadata about generation
  const metadata = (competitor as any).metadata;
  const isDraft = metadata?.isDraft;

  const editorContent = savedHtml || buildCompetitorHtml(display);

  return (
    <div className="flex flex-col h-full w-full">
      {/* FIXED HEADER - Does NOT scroll */}
      <div className="shrink-0 w-full">
        {/* Row 1: Back + Edit/Save - full width header */}
        <div className="w-full bg-white border-b border-gray-200">
          <div className="flex items-center justify-between px-8 py-4">
            <button 
              onClick={() => {
                if (isEditing) {
                  handleCancelEdit();
                  return;
                }
                onBack();
              }}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{isEditing ? 'Back' : 'Back to Competitor Analysis'}</span>
            </button>
            
            {/* Edit/Save button */}
            {isEditing ? (
              <button 
                onClick={handleSaveChanges}
                disabled={isSaving || !hasChanges()}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-lg transition-colors",
                  hasChanges() 
                    ? "text-white bg-purple-600 hover:bg-purple-700" 
                    : "text-gray-400 bg-gray-200 cursor-not-allowed",
                  isSaving && "opacity-50"
                )}
              >
                <Save className="w-4 h-4" />
                <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
              </button>
            ) : (
              <button 
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                <span>Edit</span>
              </button>
            )}
          </div>
        </div>

        {/* Row 2: Name/Subtitle + Action Buttons - full width header */}
        <div className="w-full bg-white border-b border-gray-200">
          <div className="flex items-start justify-between px-8 py-4 gap-4">
            {/* Left - Name and Subtitle */}
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold text-gray-900 truncate">{display.name}</h1>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {competitor.category && (
                  <span className={cn(
                    "px-2.5 py-0.5 text-xs font-medium rounded-full",
                    categoryColors[competitor.category]
                  )}>
                    {competitor.category.charAt(0) + competitor.category.slice(1).toLowerCase()}
                  </span>
                )}
                {competitor.threatLevel && (
                  <span className={cn(
                    "px-2.5 py-0.5 text-xs font-medium rounded-full border",
                    threatColors[competitor.threatLevel]
                  )}>
                    {competitor.threatLevel.charAt(0) + competitor.threatLevel.slice(1).toLowerCase()}
                  </span>
                )}
                {isDraft && (
                  <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    AI Draft
                  </span>
                )}
              </div>
              {/* Description subtitle */}
              {competitor.description && (
                <p className="mt-3 text-gray-600 text-sm max-w-2xl">{competitor.description}</p>
              )}
            </div>
            
            {/* Right - Action Buttons */}
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setShowAskAI(!showAskAI)}
                className="h-[42px] px-4 flex items-center gap-2 rounded-full transition-colors text-white bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 whitespace-nowrap"
              >
                <Sparkles className="w-4 h-4 shrink-0" />
                <span className="text-sm font-medium">Ask AI</span>
              </button>
              <button 
                onClick={handleCopy}
                className="h-[42px] px-4 flex items-center gap-2 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors text-gray-700 whitespace-nowrap"
              >
                <Copy className="w-4 h-4 shrink-0" />
                <span className="text-sm font-medium">Copy</span>
              </button>
            </div>
          </div>
        </div>

        {/* Ask AI Bar (also fixed when visible) */}
        {showAskAI && (
          <div className="w-full bg-gray-50 border-b border-gray-200 py-4">
            <div className="px-8">
              <AskAIBar
                isOpen={showAskAI}
                onClose={() => setShowAskAI(false)}
                onSend={handleAskAI}
                placeholder="Ask AI about this competitor..."
              />
            </div>
          </div>
        )}
      </div>

      {/* SCROLLABLE CONTENT - Only this part scrolls */}
      <div className="flex-1 min-h-0 overflow-y-auto w-full bg-gray-50">
        <div className="container px-8 py-6 space-y-6">

          {/* Main Content Card - single editor when editing */}
          <div className={cn(
            "bg-white rounded-xl mb-6 transition-all overflow-hidden",
            isEditing 
              ? "border-2 border-purple-400 shadow-[0_0_0_4px_rgba(147,51,234,0.1)]" 
              : "border border-gray-200"
          )}>
            {isEditing ? (
              <RichTextEditor
                key="editing"
                initialContent={editorContent}
                onChange={handleEditorChange}
                placeholder="Edit competitor analysis..."
                editable
                minHeight="150px"
              />
            ) : (
              <RichTextEditor
                key="readonly"
                initialContent={editorContent}
                editable={false}
                minHeight="150px"
              />
            )}
          </div>

          {/* === Sources (always read-only, outside editor) === */}
          {competitor.sourceLinks && competitor.sourceLinks.length > 0 && (
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <ExternalLink className="w-5 h-5 text-gray-400" />
                Sources
              </h2>
              <div className="space-y-2">
                {competitor.sourceLinks.map((link, idx) => (
                  <a key={idx} href={link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-brand-primary hover:underline text-sm">
                    <ExternalLink className="w-3 h-3" />{link}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* === METADATA FOOTER === */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <span className="flex items-center gap-2">
                <span className="font-medium">Created by:</span> 
                {competitor.createdBy?.name || competitor.createdBy?.email || 'Unknown'}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {new Date(competitor.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
